/**
 * All-time player leaderboard from the class summary (2010+).
 * Ranked by category fantasy points — not yards / TDs / tackles.
 */

import { FIRST_YEAR } from '../constants/app';
import type { ClassSummaryFile, ClassSummaryRow } from '../types/classSummary';
import { PLAYER, ROW } from '../types/classSummary';
import {
  ClassStatCategory,
  classRowPoints,
  classRowStatPoints,
  parseClassStatParam,
} from './classCompare';
import {
  CAREER_CATEGORY_COLUMNS,
  CAREER_SEGMENT,
  CareerFilters,
  CareerPlayerRow,
  CareerYearRange,
  matchesCareerFilters,
} from './collegeCareer';
import { resolveCollegeMeta } from './collegeMeta';

export const PLAYER_CAREER_LIMIT = 100;

export interface PlayerCareerPath {
  stat: ClassStatCategory;
}

export interface PlayerCareerTable {
  stat: ClassStatCategory;
  fromYear: number;
  toYear: number;
  rows: CareerPlayerRow[];
  matchedCount: number;
  shownCount: number;
  totalPoints: number;
  seasonCount: number;
  ytdWeek: number;
  currentSeasonIncomplete: boolean;
  currentSeason: number;
}

type CollegeAcc = {
  categoryPoints: number;
  lastSeason: number;
};

const isClassStatSegment = (value: string): boolean => {
  const stat = parseClassStatParam(value);
  return Boolean(stat) && stat === value.trim().toLowerCase();
};

export const parsePlayerCareerPath = (pathname: string): PlayerCareerPath | null => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'players' || parts[1] !== CAREER_SEGMENT) return null;
  if (parts.length > 3) return null;
  if (!parts[2]) return { stat: '' };
  if (!isClassStatSegment(parts[2])) return null;
  return { stat: parseClassStatParam(parts[2]) };
};

export const getPlayerCareerPath = (stat: string = ''): string => {
  const category = parseClassStatParam(stat);
  return category ? `/players/${CAREER_SEGMENT}/${category}` : `/players/${CAREER_SEGMENT}`;
};

const seasonSetSize = (rows: ClassSummaryRow[]): number => {
  const seasons = new Set<number>();
  rows.forEach((row) => seasons.add(row[ROW.season]));
  return seasons.size;
};

const pickCollegeId = (colleges: Map<string, CollegeAcc>): string => {
  let bestId = '';
  let bestPoints = -1;
  let bestSeason = -1;
  colleges.forEach((acc, id) => {
    if (
      acc.categoryPoints > bestPoints ||
      (acc.categoryPoints === bestPoints && acc.lastSeason > bestSeason)
    ) {
      bestId = id;
      bestPoints = acc.categoryPoints;
      bestSeason = acc.lastSeason;
    }
  });
  return bestId;
};

export const buildPlayerCareerTable = (
  summary: ClassSummaryFile,
  yearRange: CareerYearRange,
  filters: CareerFilters,
  limit: number = PLAYER_CAREER_LIMIT
): PlayerCareerTable => {
  const matchedRows: ClassSummaryRow[] = [];
  const byPlayer = new Map<
    number,
    {
      name: string;
      pos: string | null;
      posSeason: number;
      round: number | null;
      pick: number | null;
      classYear: number | null;
      seasons: Set<number>;
      categoryPoints: number;
      totalPoints: number;
      categories: number[];
      usedYtd: boolean;
      colleges: Map<string, CollegeAcc>;
    }
  >();

  for (const row of summary.rows) {
    const player = summary.players[row[ROW.playerIndex]];
    if (!player) continue;
    const classYear = player[PLAYER.classYear];
    if (!matchesCareerFilters(row, classYear, player[PLAYER.round], yearRange, filters)) continue;

    const season = row[ROW.season];
    const collegeId = String(row[ROW.collegeId]);
    matchedRows.push(row);
    const usedYtd = Boolean(summary.currentSeasonIncomplete && season === summary.toYear);
    const categoryPoints = classRowStatPoints(row, summary, filters.stat);
    const totalPoints = classRowPoints(row, summary);
    const existing = byPlayer.get(row[ROW.playerIndex]);
    if (!existing) {
      byPlayer.set(row[ROW.playerIndex], {
        name: player[PLAYER.name],
        pos: row[ROW.pos],
        posSeason: season,
        round: player[PLAYER.round],
        pick: player[PLAYER.pick],
        classYear,
        seasons: new Set([season]),
        categoryPoints,
        totalPoints,
        categories: CAREER_CATEGORY_COLUMNS.map((column) => row[column.rowIndex] || 0),
        usedYtd,
        colleges: new Map([[collegeId, { categoryPoints, lastSeason: season }]]),
      });
      continue;
    }

    existing.seasons.add(season);
    existing.categoryPoints += categoryPoints;
    existing.totalPoints += totalPoints;
    existing.usedYtd = existing.usedYtd || usedYtd;
    CAREER_CATEGORY_COLUMNS.forEach((column, index) => {
      existing.categories[index] += row[column.rowIndex] || 0;
    });
    if (season >= existing.posSeason) {
      existing.pos = row[ROW.pos];
      existing.posSeason = season;
    }
    const college = existing.colleges.get(collegeId);
    if (!college) {
      existing.colleges.set(collegeId, { categoryPoints, lastSeason: season });
    } else {
      college.categoryPoints += categoryPoints;
      if (season >= college.lastSeason) college.lastSeason = season;
    }
  }

  const allRows: CareerPlayerRow[] = Array.from(byPlayer.entries())
    .map(([playerIndex, item]) => {
      const seasons = Array.from(item.seasons).sort((a, b) => a - b);
      const college = resolveCollegeMeta(pickCollegeId(item.colleges));
      return {
        playerIndex,
        name: item.name,
        pos: item.pos,
        round: item.round,
        pick: item.pick,
        classYear: item.classYear,
        seasons: seasons.length,
        firstSeason: seasons[0],
        lastSeason: seasons[seasons.length - 1],
        categoryPoints: parseFloat(item.categoryPoints.toFixed(2)),
        totalPoints: parseFloat(item.totalPoints.toFixed(2)),
        categories: item.categories.map((value) => parseFloat(value.toFixed(2))),
        usedYtd: item.usedYtd,
        partial: item.classYear != null && item.classYear < FIRST_YEAR,
        collegeId: college.id,
        collegeSlug: college.slug,
        collegeName: college.name,
        collegeLogo: college.logo,
      };
    })
    .filter((row) => row.categoryPoints > 0)
    .sort(
      (a, b) =>
        b.categoryPoints - a.categoryPoints ||
        b.totalPoints - a.totalPoints ||
        a.name.localeCompare(b.name)
    );

  const rows = allRows.slice(0, limit);
  const totalPoints = parseFloat(
    rows.reduce((sum, row) => sum + row.categoryPoints, 0).toFixed(2)
  );

  return {
    stat: filters.stat,
    fromYear: yearRange.from,
    toYear: yearRange.to,
    rows,
    matchedCount: allRows.length,
    shownCount: rows.length,
    totalPoints,
    seasonCount: seasonSetSize(matchedRows),
    ytdWeek: summary.ytdWeek,
    currentSeasonIncomplete: summary.currentSeasonIncomplete,
    currentSeason: summary.toYear,
  };
};

export const playerCareerUsesCurrentSeason = (table: PlayerCareerTable): boolean =>
  table.currentSeasonIncomplete &&
  table.fromYear <= table.currentSeason &&
  table.toYear >= table.currentSeason;

export const playerCareerHasPartialPlayers = (table: PlayerCareerTable): boolean =>
  table.rows.some((row) => row.partial);
